// src/components/report/TrafficLightBars.tsx
import * as React from "react";
import type { FnKey } from "@/reports/narratives";

export type FnScore = { fn: FnKey; value: number }; // 0–100

const FN_ORDER: FnKey[] = ["Govern", "Identify", "Protect", "Detect", "Respond", "Recover"];

type Props = {
  scores: FnScore[];
  narratives?: Partial<Record<FnKey, string>>;
  variant?: "compact" | "full";
  showLegend?: boolean;
  /** When true, show maturity band labels instead of percentages (telecom_v2). */
  showMaturityLabel?: boolean;
};

function barColor(value: number): string {
  if (value >= 80) return "#16a34a"; // green-600 Implemented
  if (value >= 50) return "#f59e0b"; // amber-500 Partially
  if (value === 0) return "#fca5a5"; // red-300 — visible indicator for 0%
  return "#dc2626";                  // red-600 Not Implemented
}

function maturityForPct(v: number): "Minimal" | "Adequate" | "Strong" | "Extensive" {
  // Mirrors averageToMaturity on the 0-2 scale: 0-25%=Minimal, 26-50%=Adequate, 51-75%=Strong, 76-100%=Extensive.
  if (v <= 25) return "Minimal";
  if (v <= 50) return "Adequate";
  if (v <= 75) return "Strong";
  return "Extensive";
}

const MATURITY_BAR_COLOR: Record<string, string> = {
  Minimal: "#DC2626",
  Adequate: "#F59E0B",
  Strong: "#16A34A",
  Extensive: "#2563EB",
};

export function TrafficLightBars({
  scores,
  narratives,
  variant = "full",
  showLegend = true,
  showMaturityLabel = false,
}: Props) {
  const byFn = new Map<FnKey, number>();
  scores.forEach(s => byFn.set(s.fn, Math.max(0, Math.min(100, Math.round(s.value)))));

  const isCompact = variant === "compact";
  const visibleFns = FN_ORDER.filter((fn) => byFn.has(fn));

  return (
    <section className={`traffic-bars ${isCompact ? "traffic-bars--compact" : ""}`}>
      {!isCompact && (
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
          Cybersecurity Adequacy by NIST Function
        </h2>
      )}

      <div className="bars">
        {visibleFns.map((fn) => {
          const v = byFn.get(fn) ?? 0;
          const maturity = maturityForPct(v);
          const color = showMaturityLabel ? MATURITY_BAR_COLOR[maturity] : barColor(v);

          return (
            <div className="bar-block" key={fn} style={{ ["--bar-color" as any]: color }}>
              <div className="bar-row">
                <div className="bar-label">{fn}</div>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${Math.max(v, v === 0 ? 100 : 0)}%`, opacity: v === 0 ? 0.35 : 1 }} />
                </div>
                <div className="bar-percent" style={showMaturityLabel ? { color, fontWeight: 600, minWidth: 70 } : undefined}>
                  {showMaturityLabel ? maturity : `${v}%`}
                </div>
              </div>
              {!isCompact && narratives?.[fn] && (
                <div className="bar-note">{narratives[fn]}</div>
              )}
            </div>
          );
        })}
      </div>

      {showLegend && !showMaturityLabel && (
        <div className="bars-legend">
          <div className="legend-item">
            <span className="legend-dot legend-dot--implemented" aria-hidden />
            <span className="legend-text">Implemented (≥80%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot legend-dot--partial" aria-hidden />
            <span className="legend-text">Partially Implemented (50–79%)</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot legend-dot--not" aria-hidden />
            <span className="legend-text">Not Implemented (&lt;50%)</span>
          </div>
        </div>
      )}

      {showLegend && showMaturityLabel && (
        <div className="bars-legend">
          {(["Minimal", "Adequate", "Strong", "Extensive"] as const).map((m) => (
            <div className="legend-item" key={m}>
              <span
                className="legend-dot"
                style={{ background: MATURITY_BAR_COLOR[m] }}
                aria-hidden
              />
              <span className="legend-text">{m}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

